import type { ComboChains } from "./creaturemesh";
/** `NRaceData::EAttackType` (`GameType.h:39-43`). */
export const ATTACK_TYPE_SPLASH = 0;
export const ATTACK_TYPE_SNIPE = 1;

export const ATTACK_DATA_URL = "creaturemesh/attackdata.json";

/** The rest of one attack clip's blow, as `tools/build_attack_data.py` writes it
 *  from `NRaceData::LoadAttackData` (`GameType.cpp:20-40`): seconds for the two
 *  times, the physics object's force for the push. */
export interface AttackData {
  readonly attackType: number;
  readonly stiffenTimeS: number;
  readonly invisibleTimeS: number;
  readonly externalForce: number;
  readonly hitLimitCount: number;
}

/** `attackdata.json`: player rows by race id, every other body's by species
 *  name, and which race each player skin key draws. */
export interface AttackDataTable {
  readonly racePrefixes: Readonly<Record<string, number>>;
  readonly races: Readonly<Record<string, Readonly<Record<string, AttackData>>>>;
  readonly species: Readonly<Record<string, Readonly<Record<string, AttackData>>>>;
  /** Each player race's weapon combo types by race id. */
  readonly combos: Readonly<Record<string, ComboChains>>;
}

const EMPTY: AttackDataTable = { racePrefixes: {}, races: {}, species: {}, combos: {} };

/** The race a skin key draws, or null for a body that is not a player's: the
 *  pack's player key names its race outright, every other player key is
 *  `<race prefix>/<shape>`. */
export function raceOfSkinKey(prefixes: Readonly<Record<string, number>>, key: string): number | null {
  const slash = key.lastIndexOf("/");
  return prefixes[key] ?? (slash > 0 ? prefixes[key.slice(0, slash)] : undefined) ?? null;
}

/** A parsed `attackdata.json`, or an empty table for a body that holds none. */
export function attackDataTable(body: unknown): AttackDataTable {
  const table = body as Partial<AttackDataTable> | null;
  return {
    racePrefixes: table?.racePrefixes ?? {},
    races: table?.races ?? {},
    species: table?.species ?? {},
    combos: table?.combos ?? {},
  };
}

/** The blow `clip` lands when `key`'s body plays it, or null for a clip that
 *  lands none. */
export function attackDataFor(table: AttackDataTable, key: string, clip: string): AttackData | null {
  const race = raceOfSkinKey(table.racePrefixes, key);
  const rows = race !== null ? table.races[String(race)] : table.species[key];
  return rows?.[clip] ?? null;
}

/** The table, or an empty one when the file is absent or unreadable. */
export async function loadAttackData(
  fetchJson: (url: string) => Promise<Response> = (url) => fetch(url),
): Promise<AttackDataTable> {
  try {
    const response = await fetchJson(ATTACK_DATA_URL);
    if (!response.ok) return EMPTY;
    return attackDataTable(await response.json());
  } catch {
    return EMPTY;
  }
}
